"use client";

/**
 * /compare - side-by-side listing comparison.
 *
 * Query-string state model:
 *   ?ids=12,14       - comma-separated history ids (signed-in users)
 *   ?urls=<url>,...  - comma-separated, URL-encoded fresh listing URLs
 * Both can be combined. Paging back through share URLs auto-triggers
 * the same /api/compare call the user got from the manual flow, so a
 * shared link produces an identical view.
 *
 * Page layout (top-to-bottom):
 *   1. Input pane (CompareInput) - up to 4 slots.
 *   2. After submit: headline chips → AI summary → cards → trend →
 *      SHAP → table → share link.
 *
 * Token math lives on the backend - we surface the cost hint in the
 * input pane and show the existing RateLimitBanner when a
 * RateLimitError bubbles up from submitCompare().
 */
import { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useLocalizedRouter } from "@/lib/localized-router";
import { submitCompare, getRecentAnalyzed, RateLimitError, UserInputError, NetworkError } from "@/lib/api";
import { trackEvent } from "@/lib/tracking";
import { captureEvent } from "@/lib/tracking";
import { useAuth } from "@/lib/auth-context";
import { compareResultTarget } from "@/lib/compare-result-target";
import { useT } from "@/lib/locale-context";
import { captureError, captureWarning } from "@/lib/sentry";
import type { CompareResponse, CompareSlot, CompareRequestItem } from "@/types";
import MobileBottomChromeOffset from "@/components/MobileBottomChromeOffset";
import AnalysisProgress from "@/components/AnalysisProgress";
import RateLimitBanner from "@/components/RateLimitBanner";
import RecentActivity from "@/components/RecentActivity";
import CompareInput from "@/components/compare/CompareInput";
import { recordCompare } from "@/lib/recent-activity";
import HeadlineChips from "@/components/compare/HeadlineChips";
import CompareSummaryPanel from "@/components/compare/CompareSummaryPanel";
import CompareBoard from "@/components/compare/CompareBoard";
import OverlaidTrendChart from "@/components/compare/OverlaidTrendChart";
import { RisksByListing } from "@/components/compare/ComparisonTable";
import CompareRadar from "@/components/compare/CompareRadar";
import ComparePhotoReuse from "@/components/compare/ComparePhotoReuse";
import { Link2, Check, Copy, Lightbulb, AlertTriangle, Loader2 } from "lucide-react";
import CompareLanding from "@/components/compare/CompareLanding";

function newUrlSlot(value = ""): CompareSlot {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `slot_${Math.random().toString(36).slice(2, 10)}`;
  return { id, kind: "url", value };
}

function newHistorySlot(id: string): CompareSlot {
  const slotId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `slot_${Math.random().toString(36).slice(2, 10)}`;
  return { id: slotId, kind: "history_id", value: id };
}

/** Parse ?ids=1,2&urls=a,b into a slot list. Limits to 4 items -
 *  anything beyond gets dropped silently (backend also enforces). */
function parseQueryToSlots(sp: URLSearchParams): CompareSlot[] | null {
  const idsRaw = sp.get("ids") || "";
  const urlsRaw = sp.get("urls") || "";
  const ids = idsRaw ? idsRaw.split(",").filter(Boolean) : [];
  const urls = urlsRaw ? urlsRaw.split(",").filter(Boolean) : [];
  if (ids.length === 0 && urls.length === 0) return null;
  const slots: CompareSlot[] = [];
  // Preserve author order: history ids first, then URLs. Simple rule
  // keeps the shared link deterministic.
  ids.slice(0, 4).forEach((id) => slots.push(newHistorySlot(id)));
  urls.slice(0, 4 - slots.length).forEach((u) => {
    try {
      slots.push(newUrlSlot(decodeURIComponent(u)));
    } catch {
      slots.push(newUrlSlot(u));
    }
  });
  return slots;
}

function slotsToQuery(slots: CompareSlot[]): string {
  const ids: string[] = [];
  const urls: string[] = [];
  slots.forEach((s) => {
    if (s.kind === "history_id" && s.value) ids.push(s.value);
    else if (s.kind === "url" && s.value.trim()) urls.push(encodeURIComponent(s.value.trim()));
  });
  const parts: string[] = [];
  if (ids.length) parts.push(`ids=${ids.join(",")}`);
  if (urls.length) parts.push(`urls=${urls.join(",")}`);
  return parts.join("&");
}

function CompareContent() {
  const t = useT();
  const { user, refreshTokens } = useAuth();
  const searchParams = useSearchParams();
  const router = useLocalizedRouter();

  // Initial slots from query string (deep-linked share URL) or two
  // empty URL slots (fresh page load).
  const initialSlots = useMemo<CompareSlot[]>(() => {
    const fromQuery = parseQueryToSlots(searchParams);
    return fromQuery && fromQuery.length > 0 ? fromQuery : [newUrlSlot(), newUrlSlot()];
  }, [searchParams]);

  const [slots, setSlots] = useState<CompareSlot[]>(initialSlots);

  // Intent input temporarily removed; kept as an empty constant so the
  // submit payload + result-pill code paths stay intact for easy restore.
  const [userIntent] = useState<string>("");
  // Mirrors CompareInput's internal canSubmit so the page-level submit
  // button (rendered under the intent textarea) can gate on the same
  // logic without duplicating it.
  const [compareState, setCompareState] = useState<{ canSubmit: boolean }>(
    { canSubmit: false },
  );
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<CompareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const [copied, setCopied] = useState(false);
  // Synchronous re-entry guard. ``setLoading(true)`` only disables
  // the submit button after React commits the next render - on slow
  // mobile devices a rapid second tap (or panicked re-tap after an
  // error) lands inside that window and fires a duplicate POST. Same
  // pattern used by /analyze + /calculate.
  const submittingRef = useRef(false);
  // True once we've committed to router.replace toward /compare/h/[id]
  // - the loading skeleton must survive until the new page paints.
  const redirectingRef = useRef(false);

  const shareQuery = useMemo(() => slotsToQuery(slots), [slots]);

  // The form opens empty. It used to arrive with two strangers' flats
  // (the two most recently analysed listings) in the slots, which a
  // visitor had to clear before pasting their own (wave 3, owner
  // 2026-10-25). The same pair now loads only on request, from the small
  // "try an example" link under the slots.
  const [sampleBusy, setSampleBusy] = useState(false);
  const loadSample = useCallback(async () => {
    if (sampleBusy) return;
    setSampleBusy(true);
    setError(null);
    try {
      const items = await getRecentAnalyzed();
      const urls = items
        .map((it) => it.listing_url)
        .filter((u): u is string => !!u)
        .slice(0, 2);
      if (urls.length < 2) throw new Error("no sample pair");
      setSlots(urls.map((u) => newUrlSlot(u)));
      trackEvent("compare.sample", { count: urls.length });
    } catch {
      setError(t("compare.error.generic"));
    } finally {
      setSampleBusy(false);
    }
  }, [sampleBusy, t]);

  const filledCount = slots.filter((s) =>
    (s.kind === "url" && s.value.trim().length > 0) ||
    (s.kind === "history_id" && s.value),
  ).length;

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError(null);
    setRateLimitError(null);
    // Clear old response so we don't render stale cards over the new
    // loading state.
    setResponse(null);

    const items: CompareRequestItem[] = slots
      .filter((s) =>
        (s.kind === "url" && s.value.trim().length > 0) ||
        (s.kind === "history_id" && s.value),
      )
      .map((s) =>
        s.kind === "history_id"
          ? { kind: "history_id" as const, value: Number(s.value) }
          : { kind: "url" as const, value: s.value.trim() },
      );

    trackEvent("compare.submit", {
      count: items.length,
      has_intent: !!userIntent,
    });
    captureEvent("compare_submitted", {
      count: items.length,
      has_intent: !!userIntent,
      url_count: items.filter((i) => i.kind === "url").length,
      history_count: items.filter((i) => i.kind === "history_id").length,
    });

    try {
      const data = await submitCompare(items, { userIntent });
      refreshTokens();
      // Signed-in users: redirect to the persisted permalink so
      // refreshing replays from storage instead of re-submitting.
      // `router.replace` means the Back button goes past /compare
      // entirely, so the user can't accidentally land back on the
      // pre-submit form and re-run the same comparison.
      //
      // The trace_id is forwarded via a query param so the replay
      // page can open the SSE stream for the live summary on this
      // very first render (storage still has summary="" - the
      // compare/stream handler writes it back only after tokens
      // finish flowing). On refresh the trace is stale / consumed
      // and the stored summary takes over.
      // Record into recent activity BEFORE the signed-in redirect -
      // otherwise the persisted-history path returns early and the
      // compare never lands in the activity feed.
      recordCompare(data);
      // The stored result's page replays through the signed-in history
      // API, so only an account goes there. An anonymous device gets a
      // stored id as well (the admin activity feed links to it) and used
      // to be sent to that page, where it met "sign in to view"
      // (2026-10-23); it renders inline instead (lib/compare-result-target).
      const target = compareResultTarget(data, !!user);
      if (target.kind === "replay") {
        // Keep the loading skeleton up THROUGH the navigation - the
        // finally below must not flip it off, or the pre-submit form
        // flashes back for the moment router.replace takes to land
        // on the history page (owner-reported flicker).
        redirectingRef.current = true;
        router.replace(target.href, { scroll: false });
        return;
      }
      // Inline: push the slot state into the URL so a refresh/share
      // replays the set of inputs (but still requires a manual Compare
      // click - we never auto-submit from query params).
      setResponse(data);
      const qs = slotsToQuery(slots);
      if (qs) router.replace(`/compare?${qs}`, { scroll: false });
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else if (err instanceof UserInputError) {
        setError(err.message);
      } else if (err instanceof NetworkError) {
        setError(t("compare.error.generic"));
        captureWarning("Compare network error", { underlying: err.underlying });
      } else {
        const msg = err instanceof Error ? err.message : t("compare.error.generic");
        setError(msg);
        if (err instanceof Error) captureError(err);
      }
    } finally {
      if (!redirectingRef.current) setLoading(false);
      submittingRef.current = false;
    }
  }, [slots, userIntent, refreshTokens, router, t, user]);

  // Deep-linked shares (?ids=...&urls=...) land on a pre-filled
  // but NOT auto-submitted form. Explicit user click avoids the
  // "I pressed Back and it ran again" surprise - a compare run is
  // real compute (and one of an anonymous device's free analyses),
  // so submission is always manual.

  const handleCopyLink = useCallback(() => {
    if (typeof window === "undefined") return;
    const url = `${window.location.origin}/compare${shareQuery ? `?${shareQuery}` : ""}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      // Clear the "copied" toast after 2s.
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard failures are rare on modern browsers; no-op.
    });
  }, [shareQuery]);

  const results = response?.results || [];
  const errors = response?.errors || [];
  const hasResults = results.length > 0;

  // SharedHeader + CompareNav render in compare/layout.tsx (they
  // persist across tab switches; template.tsx fades this page in).
  return (
    <>
      {/* The sticky submit bar at < lg always overlaps the footer on
          mobile - lift body padding-bottom so the footer is fully
          scrollable past it. Mounted unconditionally because the bar
          itself is always rendered on /compare. */}
      <MobileBottomChromeOffset />

      <main
        className={`mx-auto ${hasResults ? "max-w-7xl" : "max-w-5xl"} px-4 sm:px-6 pb-12 space-y-6`}
      >
        {/* Header - same heading scale as the residences compare tab
            (they're peers under CompareNav). */}
        <div className="max-w-3xl">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            {t("compare.title")}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {t("compare.subtitle")}
          </p>
        </div>

        {/* Input - page-level form so the Compare button can live
            UNDERNEATH the user-intent textarea (listings → intent →
            submit, top-to-bottom). CompareInput's internal submit
            button is hidden via `hideInternalSubmit`; we mirror its
            canSubmit / cost state into local state so our button
            reflects the same gating + token cost chip.

            Hidden once the user submits: when `loading` flips on or a
            result lands, the form drops out and the loading skeleton /
            results section takes its slot at the top of the page.
            Previously the skeletons rendered UNDERNEATH the form, so
            on mobile the user had to scroll past the same form they
            just submitted to see what they were waiting on. The form
            re-appears if loading finishes without a result (network
            error, validation error, etc.) so retry is one tap away. */}
        {!loading && !hasResults && (
        <form
          onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
          className="rounded-2xl sm:border sm:border-gray-200 sm:bg-white p-0 sm:p-6 space-y-4"
        >
          <CompareInput
            slots={slots}
            onSlotsChange={setSlots}
            onSubmit={handleSubmit}
            loading={loading}
            hideInternalSubmit
            hideFooterHints
            onStateChange={setCompareState}
          />
          {/* The min-listings hint sits below the intent textarea so it
              reads as a final reminder right above the Compare CTA,
              instead of being buried between the slots and the intent
              textarea. (The per-URL cost line that stood here went with
              the analysis limits, owner 2026-10-18.) Beside it, the one
              way to see the page without a link of your own: a sample
              pair, loaded on request. */}
          {filledCount < 2 && slots.length > 0 && (
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              <span>{t("compare.input.min_listings")}</span>
              <button
                type="button"
                onClick={loadSample}
                disabled={sampleBusy}
                aria-busy={sampleBusy}
                className="no-scale inline-flex items-center gap-1 font-semibold text-gray-900 underline-offset-2 transition-colors underline decoration-gray-300 hover:decoration-gray-900 focus-visible:underline disabled:opacity-60"
              >
                {sampleBusy && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
                {t("compare.input.sample")}
              </button>
            </p>
          )}
          {/* Desktop submit - inline below the intent textarea. Mirrors
              the /calculate split: at lg+ the form fits comfortably on
              screen, so the inline button is the natural CTA. The
              mobile sticky-bottom version is rendered outside the form
              below. `loading` is included in `disabled` as belt-and-
              braces against the brief render window before the
              <form> unmounts on submit (the synchronous re-entry
              guard already handles a duplicate dispatch, but a
              visibly-clickable button during that window invites the
              user to try). */}
          <button
            type="submit"
            disabled={!compareState.canSubmit || loading}
            className="hidden lg:inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t("compare.input.loading")}
              </>
            ) : (
              t("compare.input.submit")
            )}
          </button>
        </form>
        )}

        {/* Mobile / tablet sticky submit - matches /calculate's pattern.
            On < sm (mobile) the bar lifts above MobileTabBar (z-1100) by
            its rendered height (4rem) plus env(safe-area-inset-bottom);
            sm:bottom-0 drops it flush to the bottom on tablets where the
            tab bar is already hidden. lg:hidden because at desktop the
            inline button above is already on screen. type="button" +
            explicit onClick because the button now lives outside the
            form.

            Same visibility gate as the form above: once the user
            submits, the sticky bar drops with the form. Otherwise it
            would float over the loading skeleton / results below,
            inviting a duplicate submit. */}
        {!loading && !hasResults && (
        <div className="fixed left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] lg:hidden sm:bottom-0 bottom-[calc(env(safe-area-inset-bottom)+4rem)] mb-0">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <button
              type="button"
              onClick={() => handleSubmit()}
              disabled={!compareState.canSubmit || loading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t("compare.input.loading")}
                </>
              ) : (
                t("compare.input.submit")
              )}
            </button>
          </div>
        </div>
        )}

        {/* Generic error */}
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" aria-hidden />
            <span className="min-w-0">{error}</span>
          </div>
        )}

        {/* Paywall */}
        {rateLimitError && (
          <RateLimitBanner error={rateLimitError} />
        )}

        {/* Per-item errors (bad URL in one slot, rest worked) */}
        {errors.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5 space-y-2">
            {errors.map((e) => (
              <div key={`${e.index}-${e.value}`} className="text-sm text-amber-900">
                <span className="font-semibold">{t("compare.error.item")}</span>{" "}
                <span className="text-amber-700">{e.value}</span>
                <span className="ml-2 text-amber-700">- {e.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Recent activity - hero state only (no results, not loading,
            no rate-limit blocker). Mirrors the analyze + calculate
            pages so the three entry points feel like peers. */}
        {!hasResults && !loading && !rateLimitError && (
          <>
            {/* Inviting empty-state FIRST: how it works + a preview of what
                a result looks like (real listings), so it isn't buried
                under the recent-runs list. Visible before spending a token
                and gives the page real, indexable content. */}
            <CompareLanding />
            {/* Recent runs - full width so it aligns with the form above
                instead of sitting in a narrow, off-centre column. */}
            <RecentActivity kind="compare" />
          </>
        )}

        {/* Loading state: labeled step progress (bare skeletons read
            as "stuck") above the card skeletons. */}
        {loading && !hasResults && (
          <div className="relative">
            {/* Dimmed skeleton curtain behind, progress card centered
                on the viewport above it - mirrors /analyze. */}
            <div className="pointer-events-none grid grid-cols-1 gap-4 opacity-40 sm:grid-cols-2 lg:grid-cols-4" aria-hidden>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
            <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center px-4">
              <div className="pointer-events-auto w-full max-w-xl rounded-2xl shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)]">
                <AnalysisProgress variant="compare" />
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {hasResults && (
          <>
            {/* Same-unit warning FIRST - if the compared listings are
                duplicates of each other, the buyer needs to know before
                reading anything else (the whole comparison is otherwise
                misleading). Self-hides when the items are distinct. */}
            <ComparePhotoReuse results={results} />

            {/* Intent pill - mirrors the replay page (/compare/h/[id])
                and the analyze page's user-intent marker so the buyer
                sees the same "what was asked for" signal whether they
                just submitted or came back to a saved compare. Only
                rendered when the user actually typed an intent. */}
            {userIntent.trim() && (
              <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800">
                <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
                <span className="min-w-0 break-words leading-snug">
                  <span className="font-semibold">{t("compare.replay.intent_label")}:</span>{" "}
                  {userIntent.trim()}
                </span>
              </div>
            )}

            {/* Sections cascade in (70ms stagger) instead of the whole
                result slab popping at once - same vocabulary as the
                analyze result and the listings grid. */}
            {/* Headline chips */}
            <div className="card-enter">
              <HeadlineChips results={results} />
            </div>

            {/* AI trade-off summary */}
            <div className="card-enter" style={{ animationDelay: "70ms" }}>
              <CompareSummaryPanel
                traceId={response?.trace_id ?? null}
                listingCount={results.length}
              />
            </div>

            {/* THE comparison board - one surface for the whole
                side-by-side. Columns are the listings (photo, price,
                verdict live once, in the column header), rows are the
                facts. Replaces the old cards row + mobile scoreboard
                + sectioned table triple. */}
            <div className="card-enter" style={{ animationDelay: "140ms" }}>
              <CompareBoard
                results={results}
                bestForIntentIndex={response?.best_for_intent_index ?? null}
              />
            </div>

            {/* Per-listing risk findings (prose) - self-hides when
                nothing was flagged anywhere. */}
            <div className="card-enter" style={{ animationDelay: "210ms" }}>
              {/* Strengths radar: bigger area = stronger listing (owner asked for it back). */}
              <CompareRadar results={results} />
              <RisksByListing results={results} />
            </div>

            {/* District trend, full row - the radar chart is gone
                (de-slop board 3c: no radar opener; the ledger and the
                one-sentence verdict carry the comparison). */}
            <div className="card-enter" style={{ animationDelay: "280ms" }}>
              <div>
                <OverlaidTrendChart
                  results={results}
                  mode={
                    (results[0]?.analysis_mode === "rent" ? "rent" : "sale") as "sale" | "rent"
                  }
                />
              </div>
            </div>

            {/* Share link - same card shell as other result sections,
                with a squircle header + inline copy pill matching the
                analyze/calculate ShareButton treatment. */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between card-enter" style={{ animationDelay: "350ms" }}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 w-9 h-9 flex items-center justify-center">
                  <Link2 className="w-4.5 h-4.5 text-gray-400" aria-hidden />
                </div>
                <span className="truncate text-xs sm:text-sm text-gray-500 min-w-0">
                  {typeof window !== "undefined"
                    ? `${window.location.origin}/compare${shareQuery ? `?${shareQuery}` : ""}`
                    : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopyLink}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg border transition-colors ${
                  copied
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? t("compare.share.copied") : t("compare.share.copy")}
              </button>
            </section>
          </>
        )}

      </main>
    </>
  );
}

// useSearchParams requires a Suspense boundary at the edge of the
// client page - Next 15 enforces this to unblock static prerendering.
export default function ComparePage() {
  return (
    <Suspense fallback={<div className="min-h-screen" />}>
      <CompareContent />
    </Suspense>
  );
}
